// @ts-check

import { ref, toValue, watch } from "vue";
import { toNumber, trimToUndefined } from "#ce/utils/props";

/**
 * A numeric model with two-tier change semantics: `update` for live
 * adjustments (every slider drag tick) and `commit` for settled values
 * (thumb release, a numeric-input edit). Both are deduplicated:
 *
 * - `update` no-ops when the value is already current, swallowing an inner
 *   control's echo — writing a committed value back to its value prop makes
 *   it re-emit the same number.
 * - `commit` no-ops when the value was already committed — an arrow step
 *   commits immediately, and the input's native change on blur would
 *   otherwise re-commit the same number and double the consumer's fetch.
 *
 * The external source counts as committed: when it changes, the model follows
 * and re-committing that number must not fire another commit. An empty
 * attribute value reads as "unset" (undefined), not `Number("") === 0`.
 *
 *  sets both without firing either callback.
 *
 * @param {import("vue").MaybeRefOrGetter<*>} value - the external value source (prop ref/getter)
 * @param {{ onUpdate: (value: number) => void, onCommit: (value: number) => void }} callbacks
 */
export function useCommittedModel(value, { onUpdate, onCommit }) {
  const model = ref(toNumber(trimToUndefined(toValue(value))));

  let lastCommitted = model.value;

  watch(
    () => toValue(value),
    (newValue) => {
      model.value = toNumber(trimToUndefined(newValue));
      lastCommitted = model.value;
    },
  );

  function update(newValue) {
    if (newValue === model.value) {
      return;
    }

    model.value = newValue;
    onUpdate(newValue);
  }

  function commit(newValue) {
    update(newValue);

    if (newValue === lastCommitted) {
      return;
    }

    lastCommitted = newValue;
    onCommit(newValue);
  }

  // A native control reports neither `input` nor `change` for a value it did not get from the user.
  function set(newValue) {
    model.value = newValue;
    lastCommitted = newValue;
  }

  return { model, update, commit, set };
}
